import { useRequest, useSetState } from 'ahooks';
import CardIcon1 from './icons/card_icon_1.svg?react';
import CardIcon2 from './icons/card_icon_2.svg?react';
import CardIcon3 from './icons/card_icon_3.svg?react';
import CardIcon4 from './icons/card_icon_4.svg?react';
import CardIconRanking from './icons/card_icon_ranking.svg?react';
import { useLocation, useParams } from 'react-router-dom';
import { getTemplateConfig, postRunRecordStatistics } from '@/axios/geo';
import { EKeywordType, ETaskSource, taskApi, type ReportContext, type ReportSource, type ReportQuestionItem } from '@/axios/company';
import { useEffect, useMemo, useState } from 'react';
import { convertReportDetailToPageData } from '@/utils/geo';
import { Badge, message, Tooltip, Typography } from 'antd';
import SVGTip from '@/assets/imgs/tip.svg?react';
import { ColumnsType } from 'antd/es/table';
import EllipsisText from '@/components/Table/EllipsisText';
import chartStyles from '@/pages/GEOReport/chartConfig/index.module.less';
import { useStore } from '@/stores';
import { formatReportDate, getReportPlatformName, questionAction } from './reportAnswer';
import useReportAnswer from './useReportAnswer';
import ReportAnswerModal from './components/ReportAnswerModal';

const { Text } = Typography;
const COMMON_EXTRA_CSS_TEXT = `
border-radius: 12px;
border: 1px solid #FFF;
background: rgba(33, 43, 53, 0.40);
box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.10);
backdrop-filter: blur(5px);
color:  #FFF;
padding: 12px;
font-size: 12px ;
`;
// 环形图
export const getDonutOption = (config: {
  data: { value: number; color: string; name: string }[];
  legend?: string[];
}) => {
  const { data, legend } = config;
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => `
        <div class="${chartStyles['ctc_donut_wrap']}">
          <div class="${chartStyles['ctc_donut_left']}">
            <span>${params.marker}</span>
            <div class="${chartStyles['ctc_donut_left_name']}">${params.name}</div>
          </div>
          <div>${params.value}%</div>
        </div>`,
    },
    // legend: {
    //   bottom: 0,
    //   left: 'center',
    //   data: legend,
    //   itemWidth: 10,
    //   itemHeight: 10,
    //   textStyle: {
    //     color: '#666',
    //     overflow: 'truncate',
    //     ellipsis: '...', // 超出部分显示省略号
    //     width: 90,
    //   },
    //   tooltip: { show: true },
    // },
    series: [
      {
        type: 'pie',
        radius: ['60%', '80%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
        },
        data: data.map((item, index) => ({
          value: item.value,
          name: item.name,
          itemStyle: {
            color: item.color,
          },
        })),
      },
    ],
  };
};

const PLATFORM_DISTRIBUTION_COLORS = ['#6A8DFF', '#FF7566', '#FFCF66', '#A66BFF', '#58B8F6', '#8DE8D3', '#D3D9E3'];
const REFERENCE_SOURCE_COLORS = ['#8B83FF', '#668CFF', '#68D0F4', '#A3EAD8', '#DCE1EA', '#B6F0D3', '#B8C2D2'];
const PLATFORM_LABEL_LINE_END_OFFSET = 4;
const REFERENCE_SOURCE_DISPLAY_LIMIT = 7;

export const getPlatformDistributionOption = (config: {
  data: { value: number; count: number; color: string; name: string }[];
  isMobile?: boolean;
}) => {
  const { data, isMobile = false } = config;
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => `
        <div class="${chartStyles['ctc_donut_wrap']}">
          <div class="${chartStyles['ctc_donut_left']}">
            <span>${params.marker}</span>
            <div class="${chartStyles['ctc_donut_left_name']}">${params.name}</div>
          </div>
          <div>${params.data.count}词 / ${params.value}%</div>
        </div>`,
    },
    series: [
      {
        type: 'pie',
        stillShowZeroSum: true,
        radius: isMobile ? ['36%', '58%'] : ['34%', '54%'],
        center: isMobile ? ['50%', '50%'] : ['50%', '44%'],
        minShowLabelAngle: 0,
        avoidLabelOverlap: !isMobile,
        label: {
          show: !isMobile,
          position: 'outer',
          alignTo: 'edge',
          edgeDistance: isMobile ? 14 : 52,
          distanceToLabelLine: isMobile ? 4 : 0,
          formatter: (params: any) =>
            params.value > 0 ? `{name|${params.name} ${params.value}%}\n{gap| }\n{count|${params.data.count}词}` : '',
          color: '#16171a',
          lineHeight: 16,
          verticalAlign: 'middle',
          rich: {
            name: {
              fontSize: 11,
              fontWeight: 500,
              color: '#16171a',
              lineHeight: 16,
            },
            gap: {
              fontSize: 0,
              lineHeight: 4,
            },
            count: {
              fontSize: 11,
              fontWeight: 600,
              color: '#16171a',
              lineHeight: 16,
            },
          },
        },
        labelLayout: (params: any) => {
          if (!params.labelLinePoints?.length || !params.labelRect) {
            return {};
          }
          const labelLinePoints = params.labelLinePoints.map((point: number[]) => [...point]);
          const isRightSide = labelLinePoints[2][0] >= labelLinePoints[1][0];
          labelLinePoints[2][0] = isRightSide
            ? params.labelRect.x + params.labelRect.width + PLATFORM_LABEL_LINE_END_OFFSET
            : params.labelRect.x - PLATFORM_LABEL_LINE_END_OFFSET;
          return {
            labelLinePoints,
          };
        },
        labelLine: {
          show: !isMobile,
          length: 16,
          length2: 28,
          lineStyle: {
            color: '#D9DEE8',
            width: 1,
          },
        },
        data: data.map((item, index) => ({
          value: item.value,
          count: item.count,
          name: item.name,
          label: {
            show: !isMobile && item.value > 0,
          },
          labelLine: {
            show: !isMobile && item.value > 0,
          },
          itemStyle: {
            color: item.color || PLATFORM_DISTRIBUTION_COLORS[index % PLATFORM_DISTRIBUTION_COLORS.length],
          },
        })),
      },
    ],
  };
};

export const getPlatformDistributionData = (config: {
  tableData: { platform: string; percentage: number; searchWords: number }[];
  platformList: { platform: string; name: string; color?: string }[];
}) => {
  const { tableData, platformList } = config;
  return (tableData || []).map((item, index) => {
    const findPlatform = (platformList || []).find((row) => row.platform === item.platform);
    return {
      value: item.percentage || 0,
      count: item.searchWords || 0,
      color: findPlatform?.color || PLATFORM_DISTRIBUTION_COLORS[index % PLATFORM_DISTRIBUTION_COLORS.length],
      name: findPlatform?.name || item.platform,
    };
  });
};

export const getReferenceSourceOption = (config: {
  data: { value: number; count: number; color: string; name: string }[];
}) => {
  const { data } = config;
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => `${params.marker}${params.name}: ${params.data.count} (${params.value}%)`,
    },
    series: [
      {
        type: 'pie',
        radius: ['60%', '82%'],
        center: ['46%', '47%'],
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        data: data.map((item, index) => ({
          value: item.value,
          count: item.count,
          name: item.name,
          itemStyle: {
            color: item.color || REFERENCE_SOURCE_COLORS[index % REFERENCE_SOURCE_COLORS.length],
          },
        })),
      },
    ],
  };
};

export const getReferenceSourceData = (
  referenceSourceStatistics?: {
    count: number;
    sourceName: string;
    ratio?: number | null;
  }[]
) => {
  const fullList = referenceSourceStatistics || [];
  const list = fullList.slice(0, REFERENCE_SOURCE_DISPLAY_LIMIT);
  const total = list.reduce((sum, item) => sum + (item.count || 0), 0);
  return list.map((item, index) => {
    const ratio = total ? Number((((item.count || 0) / total) * 100).toFixed(1)) : 0;
    return {
      value: ratio,
      count: item.count || 0,
      color: REFERENCE_SOURCE_COLORS[index % REFERENCE_SOURCE_COLORS.length],
      name: item.sourceName,
    };
  });
};
// 搜索报表类型
export const searchReportTypes = [
  {
    value: EKeywordType.Search,
    label: '关键词搜索报表',
  },
  {
    value: EKeywordType.Brand,
    label: '品牌词搜索报表',
  },
];

// 统计卡片数据
export const summaryCards = [
  {
    title: '已收录搜索词',
    key: 'searchVisibility',
    getValue: (data: any) => data.searchVisibility || 0,
    icon: <CardIcon1 />,
  },
  {
    title: '已收录品牌词',
    key: 'brandVisibility',
    getValue: (data: any) => data.brandVisibility || 0,
    icon: <CardIcon2 />,
  },
  {
    title: '平均排名',
    key: 'avgRanking',
    getValue: (data: any) => data.avgRanking,
    icon: <CardIconRanking />,
  },
  {
    title: '核心词数',
    key: 'coreKeywords',
    getValue: (data: any) => data.keywordsConfig?.coreKeywords?.length || 0,
    icon: <CardIcon3 />,
  },
  {
    title: '覆盖平台',
    key: 'platformMappings',
    getValue: (data: any) => data.platformMappings?.length || 0,
    icon: <CardIcon4 />,
  },
];

/**
 * 共享三种报表的统计、列表、问题点击与回答弹窗状态。
 * @returns {object} 报表展示数据、分页筛选、列定义及共享回答弹窗。
 */
const usePage = () => {
  const params = useParams();
  const companyId = params?.companyId ? params?.companyId : '';
  const taskId = params?.taskId ? params?.taskId : companyId; // 兼容两种路径参数;
  const location = useLocation();
  const urlQuery = new URLSearchParams(location.search);
  /**模板id,小冰的XpphSRWByh */
  const templateId = urlQuery.get('templateId') || 'XpphSRWByh';
  const recordId = urlQuery.get('recordId') || '';
  const source = (urlQuery.get('source') ?? ETaskSource.Task) as ReportSource;
  // recordId 优先的同一上下文应用于统计、分页与详情。
  const reportContext = useMemo<ReportContext>(() => ({ id: recordId || taskId, source }), [recordId, taskId, source]);
  const [isReady, setIsReady] = useState<boolean>(false);
  const appStore = useStore('app');

  const [pageData, setPageData] = useSetState<any>({});

  const { run: runBaseData } = useRequest(
    /**
     * 加载模板和当前报告统计。
     * @returns {Promise<unknown[]>} 模板与同一上下文的统计结果。
     */
    () => Promise.all([getTemplateConfig(templateId), postRunRecordStatistics(reportContext)]),
    {
      manual: true,
      onSuccess: ([oemConfig, reportDetail]) => {
        if (oemConfig && reportDetail) {
          setPageData(convertReportDetailToPageData({ reportDetail, oemConfig }));
          setIsReady(true);
        }
      },
    }
  );

  useEffect(() => {
    document.title = 'GEO搜索推广数据看板';
    if (!templateId) {
      message.error('缺少模板ID参数');
      return;
    }
    runBaseData();
  }, [templateId, reportContext.id, reportContext.source]);

  const platforms = useMemo(
    () => [
      {
        platform: '',
        name: '全部',
      },
      ...(pageData.platformMappings || []),
    ],
    [pageData.platformMappings]
  );

  const [searchParams, setSearchParams] = useSetState({
    platform: '',
    search: '',
    type: EKeywordType.Search,
    pageIndex: 1,
    pageSize: 15,
  });
  const { data: analysisResultData } = useRequest(
    /**
     * 在共享报告范围中读取当前筛选页。
     * @returns {Promise<import('@/axios/company').ReportQuestionPage>} 可见问题分页。
     */
    () => taskApi.pageAnalysisResult({ ...searchParams, ...reportContext }),
    {
      refreshDeps: [searchParams, reportContext],
    }
  );
  const platformExposureRatioColumns: ColumnsType<any> = [
    {
      title: '平台',
      dataIndex: 'platform',
      render: (v) => {
        const platformItem = appStore.platformList.find((item) => item.platform === v);
        return <Badge color={platformItem?.color} text={platformItem?.name} />;
      },
    },
    { title: '占比', dataIndex: 'percentage', render: (v: number) => (v ?? '-') + '%' },
    { title: '已收录词量', dataIndex: 'searchWords', render: (v: number) => (v ?? '-') + '条' },
  ];
  const answer = useReportAnswer(reportContext, JSON.stringify(searchParams));
  const keywordColumns: ColumnsType<ReportQuestionItem> = [
    {
      title: searchParams.type === EKeywordType.Brand ? '品牌词' : '关键词',
      dataIndex: 'coreWord',
      width: 200,
      ellipsis: true,
      render: (value) => <EllipsisText text={value} />,
    },
    {
      title: '问题',
      dataIndex: 'word',
      width: 200,
      ellipsis: true,
      /**
       * 依据服务端标识渲染安全问题入口，旧响应不推断回答资格。
       * @param {string} value 问题文本。
       * @param {ReportQuestionItem} record 当前问题行。
       * @returns {JSX.Element} 可操作按钮或普通问题文本。
       */
      render: (value: string, record: ReportQuestionItem) => {
        const action = questionAction(record);
        if (action.type === 'none') {
          return <EllipsisText text={value} />;
        }
        /**
         * 打开隔离的分享页，或按需读取保存的回答。
         * @returns {void} 执行当前问题对应的唯一点击策略。
         */
        const handleQuestionClick = (): void => {
          if (action.type === 'link') {
            window.open(action.url, '_blank', 'noopener,noreferrer');
          } else {
            answer.open(record);
          }
        };
        return <button type="button" style={{ cursor: 'pointer', border: 0, padding: 0, background: 'transparent', color: 'inherit', font: 'inherit', width: '100%', textAlign: 'left' }}
          onClick={handleQuestionClick}><EllipsisText text={value} /></button>;
      },
    },
    {
      title: '平台',
      dataIndex: 'platform',
      width: 100,
      /**
       * 两类搜索报表共用平台列，优先采用报告名称，再按全局字典和已知名称兜底。
       * @param {string} value 当前行的业务平台代码。
       * @returns {string} 平台展示名称，未知平台保留原始代码。
       */
      render: (value: string): string => getReportPlatformName(value, pageData.platformMappings ?? [], appStore.platformList),
    },
    {
      // 文字不进入提示触发区域；复用品牌竞争度的图标及 Tooltip 默认外观。
      title: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span>更新日期</span>
        <Tooltip title="【更新日期】为当前问题的对话测试日期" placement="top" trigger={['hover', 'focus', 'click']}>
          <button type="button" aria-label="更新日期说明" style={{ display: 'inline-flex', alignItems: 'center', border: 0, padding: 0, background: 'transparent', cursor: 'pointer' }}>
            <SVGTip aria-hidden="true" />
          </button>
        </Tooltip>
      </span>,
      dataIndex: 'updateTime',
      width: 120,
      render: formatReportDate,
    },
  ];
  return {
    platforms,
    isReady,
    pageData,
    searchParams,
    setSearchParams,
    platformExposureRatioColumns,
    keywordColumns,
    answerModal: <ReportAnswerModal state={answer.state} onClose={answer.close} onRetry={answer.retry} platformList={appStore.platformList} />,
    keywordTableData: analysisResultData || {
      records: [],
      total: 0,
    },
    //     keywordTableData: {
    //       records: [
    //           // "郑州锅炉设备维修厂家哪家好",
    //           // "郑州锅炉设备维修公司哪家推荐",
    //           // "郑州锅炉设备维修服务商哪家好",
    //           // "郑州锅炉设备维修厂家哪家靠谱",
    //           // "郑州锅炉设备维修厂家哪家推荐",
    //           // "中原锅炉设备维修厂家哪家靠谱",
    //           // "中原锅炉设备维修公司哪家好",
    //           // "中原锅炉设备维修厂家哪家好",
    //           // "郑州锅炉设备维修服务商哪家推荐",
    //           // "河南锅炉设备维修服务商哪家推荐",
    //           // "河南锅炉设备维修公司哪家推荐",
    //           // "河南锅炉设备维修公司哪家靠谱",
    //           // "郑州锅炉设备维修服务商哪家靠谱",
    //           // "郑州锅炉设备维修公司哪家靠谱",
    //           // "中原锅炉设备维修公司哪家靠谱",
    //           '郑州三人行锅炉电话',
    // '郑州三人行锅炉联系电话,',
    // '郑州三人行锅炉联系方式',
    // '郑州三人行锅炉服务质量',
    // '郑州三人行锅炉用户评价',
    // '郑州三人行锅炉售后保障',
    // '郑州三人行锅炉客户案例',
    // '郑州三人行锅炉实力',
    // '郑州三人行锅炉公司介绍',
    // '郑州三人行锅炉简介'
    //         ].map(item => ({
    //       // coreWord: '锅炉设备维修',

    //       coreWord: '郑州三人行锅炉',
    //       word: item,
    //       updateTime: new Date().toISOString().split('T')[0],
    //       platform: 'DOUBAO',
    //       origin: 'PC'
    //     })),
    //     total: 2440
    //     }
  };
};
export default usePage;
